/* eslint-disable @typescript-eslint/no-deprecated -- the component consumes its own
   deprecated props (armory's `primary`/`secondary` flags, `labelClassName` and the
   MUI ripple no-ops) so armory call sites port unchanged. */
import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { LoadingDots } from './LoadingDots';
import { cn } from '../../utils/cn';
import { Color } from '../../utils/color';
import { Privacy } from '../../utils/privacy';

/**
 * Button — armory's `Button` (sanctuary-turbo/packages/armory/src/lib/Button)
 * rebuilt on Base UI's Button part.
 *
 * Colour states follow the **Forge 2.0** Button set (node 1666:1; ruling
 * 2026-09-03): rest, hovered, pressed and disabled for its eight types
 * (Primary / Outlined / Tonal / Minimal and their Warning twins). The Forge
 * draws hover and pressed as shades of the fill — `Secondary/darkShade/16`
 * and `/32` — so the dark states are `color-mix()` shades of the surface
 * token (`surface-action-hover` / `-pressed`) and a firm accent's button
 * darkens from the accent, not to a Signal colour. Light states (Outlined,
 * Minimal and Tonal fills) bind the ramp steps the Forge binds
 * (`lightShade/8 · 24 · 48` = `primary-200 / 500 / 700`; the error palette's
 * `8 · 16 · 32` = `danger-200 / 300 / 500`). Disabled is design's
 * `Neutral/Disabled` ink on `Surface/Disabled`, with `Border/Primary` as the
 * outlined border. Recorded gap: the Forge's Outlined rest border binds
 * `lightShade/48` (`primary-700`) where design's `Border/Action` token is 500.
 *
 * armory supplies what the Forge does not define: geometry and type per size
 * (the Forge's Default is 12px inset where armory's medium is 16px — kept as
 * armory, a recorded gap), the `color` enum (its non-design hues take the same
 * shade rule), loading and the deprecated prop surface — except the loading
 * indicator itself: three dots breathing in sequence (`LoadingDots`) replace
 * armory's stepping text ellipsis (ruling, 2026-09-03). Deltas that are not
 * pixels: no ripple, a focus-visible outline in place of MUI's
 * ripple-plus-elevation focus cue, and native `disabled` by default (ruling 8;
 * `focusableWhenDisabled` is the opt-in for tooltip hosts) in place of the
 * focusable wrapper `<span>` armory nested around disabled buttons. `href`
 * renders an `<a>` directly on the same classes: Base UI's Button enforces
 * button semantics and its docs keep links out of its `render` prop.
 */
export const buttonVariants = cva(
  [
    // Geometry and type shared by every variant. Heights are fixed boxes
    // (32 / 40 / 56) rather than armory's padding + line-height arithmetic;
    // flex centering keeps the glyph where MUI put it.
    // Tailwind v4's preflight resets <button> to `cursor: default`; MUI ButtonBase (armory) was `pointer`.
    // `display` is the noWrap variant's (inline-flex, or block when truncating): one class per
    // property per state, because cn() resolves no conflicts (ADR 021).
    'min-w-16 cursor-pointer items-center justify-center gap-2 rounded-sm select-none',
    // armory: background-color, color, border-color 300ms cubic-bezier(0, 0, 0.2, 1) — the exact
    // property list, not `transition-colors`, which would also animate the focus outline.
    'transition-[background-color,color,border-color] duration-300 ease-out',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface-action',
    // MUI ButtonBase.Mui-disabled: pointer-events none; the Forge's Neutral/Disabled ink
    'data-disabled:pointer-events-none data-disabled:cursor-default data-disabled:text-text-disabled',
  ],
  {
    variants: {
      variant: {
        // Disabled per the Forge: Surface/Disabled fill (contained, tonal), a
        // Border/Primary hairline and no fill (outlined), ink only (text).
        contained: 'data-disabled:bg-surface-disabled',
        outlined: 'border data-disabled:border-border-primary data-disabled:bg-transparent',
        text: '',
        // Tonal's colours are compound entries below, one per warning state, like the other
        // three variants; only its disabled fill lives on the axis.
        tonal: 'data-disabled:bg-surface-disabled',
      },
      color: {
        primary: '',
        secondary: '',
        inherit: '',
        error: '',
        info: '',
        success: '',
        warning: '',
      },
      size: {
        small: 'h-8 px-3 text-subtitle2',
        medium: 'h-10 px-4 text-subtitle2',
        large: 'h-14 px-7 text-subtitle1',
      },
      warning: { true: '', false: '' },
      fullWidth: { true: 'w-full', false: '' },
      // armory noWrap: white-space nowrap, text-overflow ellipsis, overflow
      // hidden, display block (yes, block — the root stops being inline-flex).
      // The variant owns `display`, so the two classes never share an element.
      noWrap: { true: 'block truncate', false: 'inline-flex' },
    },
    compoundVariants: [
      // contained × color — the Forge's Primary: fill Main, ink white; hover and pressed
      // are the 16% / 32% dark shades of the fill (Secondary/darkShade/16 · 32), so a firm
      // accent's states darken from the accent. armory's other hues take the same rule.
      {
        variant: 'contained',
        color: ['secondary', 'info'],
        warning: false,
        class:
          'bg-surface-action text-text-on-action hover:bg-surface-action-hover active:bg-surface-action-pressed',
      },
      {
        variant: 'contained',
        color: 'primary',
        warning: false,
        class:
          'bg-surface-inverse text-text-on-inverse hover:bg-surface-inverse-hover active:bg-surface-inverse-pressed',
      },
      // The status fills keep a white label under every brand: armory pins error /
      // success contrastText to white, while Type/Colors/On-Action follows the firm
      // accent's contrast (--brand-on-accent). Design names no on-status role yet, so
      // the ink is the semantic step — neutral-0 never rethemes; filed.
      {
        variant: 'contained',
        color: 'error',
        warning: false,
        class:
          'bg-surface-danger text-neutral-0 hover:bg-surface-danger-hover active:bg-surface-danger-pressed',
      },
      {
        variant: 'contained',
        color: 'success',
        warning: false,
        class:
          'bg-surface-success text-neutral-0 hover:bg-surface-success-hover active:bg-surface-success-pressed',
      },
      {
        variant: 'contained',
        color: 'warning',
        warning: false,
        class:
          'bg-surface-warning text-text-primary hover:bg-surface-warning-hover active:bg-surface-warning-pressed',
      },
      // MUI's contained `inherit` is a neutral grey button (grey[300]). No Forge variant
      // and no portal call site; the neutral ramp's 300 / 400 / 500 stand in.
      {
        variant: 'contained',
        color: 'inherit',
        warning: false,
        class: 'bg-neutral-300 text-text-primary hover:bg-neutral-400 active:bg-neutral-500',
      },
      // outlined × color — the Forge's Outlined: ink Main; the rest border is the hue's
      // lightShade/48 (primary-700; design's Border/Action token is 500 — recorded gap),
      // hover and pressed fill lightShade/8 · 24 and the border goes to Main. The status
      // hues follow the Forge's Warning Outlined: Border/<hue> at rest, lightShade/8 · 16.
      {
        variant: 'outlined',
        color: ['secondary', 'info'],
        warning: false,
        class:
          'border-primary-700 text-text-action hover:border-current hover:bg-surface-action-light active:border-current active:bg-primary-500',
      },
      {
        variant: 'outlined',
        color: 'primary',
        warning: false,
        class:
          'border-neutral-400 text-text-primary hover:border-current hover:bg-neutral-200 active:border-current active:bg-neutral-300',
      },
      {
        variant: 'outlined',
        color: 'error',
        warning: false,
        class:
          'border-border-danger text-text-danger hover:border-current hover:bg-surface-danger-light active:border-current active:bg-danger-300',
      },
      {
        variant: 'outlined',
        color: 'success',
        warning: false,
        class:
          'border-border-success text-text-success hover:border-current hover:bg-surface-success-light active:border-current active:bg-success-300',
      },
      {
        variant: 'outlined',
        color: 'warning',
        warning: false,
        class:
          'border-border-warning text-text-warning hover:border-current hover:bg-surface-warning-light active:border-current active:bg-warning-300',
      },
      {
        variant: 'outlined',
        color: 'inherit',
        warning: false,
        class: 'border-current text-current hover:bg-neutral-200 active:bg-neutral-300',
      },
      // text × color — the Forge's Minimal: ink Main, hover / pressed fill lightShade/8 · 24
      // (the status hues: 8 · 16, as the Forge's Warning Minimal).
      {
        variant: 'text',
        color: ['secondary', 'info'],
        warning: false,
        class: 'text-text-action hover:bg-surface-action-light active:bg-primary-500',
      },
      {
        variant: 'text',
        color: 'primary',
        warning: false,
        class: 'text-text-primary hover:bg-neutral-200 active:bg-neutral-300',
      },
      {
        variant: 'text',
        color: 'error',
        warning: false,
        class: 'text-text-danger hover:bg-surface-danger-light active:bg-danger-300',
      },
      {
        variant: 'text',
        color: 'success',
        warning: false,
        class: 'text-text-success hover:bg-surface-success-light active:bg-success-300',
      },
      {
        variant: 'text',
        color: 'warning',
        warning: false,
        class: 'text-text-warning hover:bg-surface-warning-light active:bg-warning-300',
      },
      {
        variant: 'text',
        color: 'inherit',
        warning: false,
        class: 'text-current hover:bg-neutral-200 active:bg-neutral-300',
      },
      // tonal — the Forge's Tonal: Secondary/lightShade/8 · 24 · 48 (primary-200 / 500 / 700),
      // ink Secondary/Main; the colour prop does not reach it, exactly as in armory.
      {
        variant: 'tonal',
        warning: false,
        class:
          'bg-surface-action-light text-text-action hover:bg-surface-action-light-hover active:bg-primary-700',
      },
      // warning — the Forge's four Warning types (the error palette), whatever the colour
      // prop says: Error/darkShade/16 · 32 for the fill, lightShade/8 · 16 · 32 for the tints.
      {
        variant: 'contained',
        warning: true,
        class:
          'bg-surface-danger text-neutral-0 hover:bg-surface-danger-hover active:bg-surface-danger-pressed',
      },
      {
        variant: 'outlined',
        warning: true,
        class:
          'border-border-danger text-text-danger hover:border-current hover:bg-surface-danger-light active:border-current active:bg-danger-300',
      },
      {
        variant: 'text',
        warning: true,
        class: 'text-text-danger hover:bg-surface-danger-light active:bg-danger-300',
      },
      {
        variant: 'tonal',
        warning: true,
        class: 'bg-surface-danger-light text-text-danger hover:bg-danger-300 active:bg-danger-500',
      },
    ],
    defaultVariants: {
      variant: 'contained',
      color: 'secondary',
      size: 'medium',
      warning: false,
      fullWidth: false,
      noWrap: false,
    },
  },
);

type ButtonCva = VariantProps<typeof buttonVariants>;

export type ButtonVariant = NonNullable<ButtonCva['variant']>;
export type ButtonSize = NonNullable<ButtonCva['size']>;

/** MUI start/end icon slots: 8px gap (the root's `gap-2`), a negative outer
 * margin, and glyph size by button size (18 / 20 / 22). */
const iconSlotClasses = {
  small: { start: '-ml-0.5 [&>svg]:size-4.5', end: '-mr-0.5 [&>svg]:size-4.5' },
  medium: { start: '-ml-1 [&>svg]:size-5', end: '-mr-1 [&>svg]:size-5' },
  large: { start: '-ml-1 [&>svg]:size-5.5', end: '-mr-1 [&>svg]:size-5.5' },
} satisfies Record<ButtonSize, { start: string; end: string }>;

/**
 * Base UI types every event handler against `HTMLButtonElement`; the `href` path
 * renders an `<a>` on the same props, so the handlers are re-typed against the
 * shared `HTMLElement`, which both elements' handler slots accept.
 */
type ButtonElementProps = Omit<
  ButtonPrimitive.Props,
  'className' | 'color' | 'ref' | 'style' | 'type' | keyof React.DOMAttributes<HTMLButtonElement>
> &
  Omit<React.DOMAttributes<HTMLElement>, 'onBlur' | 'onClick'>;

export interface ButtonProps extends ButtonElementProps {
  /** The rendered element: a `<button>`, or an `<a>` when `href` is set. */
  ref?: React.Ref<HTMLElement>;
  /** A class to be applied to the button element. */
  className?: string;
  /** Inline styles for the button element (a plain object, as in armory; Base UI's state-function form is not part of this contract). */
  style?: React.CSSProperties;
  /** @deprecated Use className instead (the label element was removed). Applied to the button element. */
  labelClassName?: string;
  /** Type of HTML button. */
  type?: 'submit' | 'button';
  /** Callback to be invoked when the button is clicked. */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Disables the button so it cannot be clicked. */
  isDisabled?: boolean;
  /** The style variant of the button. */
  variant?: ButtonVariant;
  /** @deprecated Legacy flag that only sets variant to 'contained'. Use variant='contained' and the color prop instead. */
  primary?: boolean;
  /** @deprecated Legacy flag that only sets variant to 'outlined'. Use variant='outlined' and the color prop instead. */
  secondary?: boolean;
  /** The palette role of the button (use the `Color` object). */
  color?: Color;
  /** The size of the button. */
  size?: ButtonSize;
  /** Whether the text of the button should refuse to wrap (truncates with an ellipsis). */
  noWrap?: boolean;
  /** @deprecated MUI ripple no-op, kept so call sites compile. This button has no ripple. */
  disableRipple?: boolean;
  /** @deprecated MUI ripple no-op, kept so call sites compile. This button has no ripple. */
  disableFocusRipple?: boolean;
  /** A class to be applied to the button when disabled. */
  disabledClassName?: string;
  /** Element placed before the children. */
  startIcon?: React.ReactNode;
  /** A class to be applied to the start icon slot. */
  startIconClassName?: string;
  /** Element placed after the children. */
  endIcon?: React.ReactNode;
  /** A class to be applied to the end icon slot. */
  endIconClassName?: string;
  /** Callback for when the button loses focus. */
  onBlur?: (event: React.FocusEvent<unknown>) => void;
  /** Sets the warning (error palette) state for the button. */
  warning?: boolean;
  /** Session-replay privacy level, emitted as classes on the button. Defaults to unmask. */
  privacyLevel?: Privacy;
  /**
   * When true, shows `loadingText` with three breathing dots and disables the
   * button. The button does not shrink while loading.
   */
  isLoading?: boolean;
  /** Text to display when `isLoading` is true. Defaults to "Loading". */
  loadingText?: string;
  /** @deprecated Firm branding now lives in the token layer; this flag is accepted and ignored. */
  disableBrandColorRemap?: boolean;
  /** Stretches the button to its container's width. */
  fullWidth?: boolean;
  /** Renders the button as an anchor pointing at this URL. */
  href?: string;
}

/**
 * A lightweight button. Use `variant`, `color`, `size` and `warning` for the
 * look; `isDisabled` / `isLoading` for state; `startIcon` / `endIcon` for
 * adornments. Base UI's `render` prop replaces MUI's `component`.
 */
export function Button(props: ButtonProps) {
  const {
    children,
    className,
    color = Color.secondary,
    disabled = false,
    disabledClassName,
    disableBrandColorRemap: _disableBrandColorRemap,
    disableFocusRipple: _disableFocusRipple,
    disableRipple: _disableRipple,
    endIcon,
    endIconClassName,
    focusableWhenDisabled,
    fullWidth = false,
    href,
    isDisabled = false,
    isLoading = false,
    labelClassName,
    loadingText = 'Loading',
    nativeButton,
    noWrap = false,
    onClick,
    primary = false,
    privacyLevel = Privacy.unmask,
    ref,
    render,
    secondary = false,
    size = 'medium',
    startIcon,
    startIconClassName,
    style,
    type = 'button',
    variant = 'contained',
    warning = false,
    ...others
  } = props;

  // armory: `primary ? 'contained' : secondary ? 'outlined' : variant`
  const resolvedVariant: ButtonVariant = primary ? 'contained' : secondary ? 'outlined' : variant;
  // Loading disables the button to prevent double clicks. armory ignored the
  // native `disabled` prop in favour of `isDisabled`; honouring both is the one
  // behavioural addition here.
  const effectivelyDisabled = disabled || isDisabled || isLoading;

  // Width lock: remember the last non-loading width and pin min-width to it
  // while loading so the label swap never shrinks the button.
  const rootRef = React.useRef<HTMLElement | null>(null);
  const lastNonLoadingWidth = React.useRef<number | undefined>(undefined);
  const widthLocked = React.useRef(false);

  const setRootRef = React.useCallback(
    (node: HTMLElement | null) => {
      rootRef.current = node;
      if (typeof ref === 'function') {
        const cleanup = ref(node);
        return () => {
          rootRef.current = null;
          if (typeof cleanup === 'function') {
            cleanup();
          } else {
            ref(null);
          }
        };
      }
      if (ref) {
        ref.current = node;
      }
      return () => {
        rootRef.current = null;
        if (ref) {
          ref.current = null;
        }
      };
    },
    [ref],
  );

  // Capture the width at the moment of the click, before the click flips
  // `isLoading` upstream, exactly as armory did.
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isLoading && rootRef.current) {
      lastNonLoadingWidth.current = rootRef.current.offsetWidth;
    }
    onClick?.(event);
  };

  // Also capture when loading starts without a click, and apply / release the
  // lock before paint. Runs every render on purpose: the width can change with
  // the label, and the read is cheap.
  React.useLayoutEffect(() => {
    const element = rootRef.current;
    if (!element) {
      return;
    }
    if (isLoading) {
      if (lastNonLoadingWidth.current !== undefined) {
        element.style.minWidth = `${String(lastNonLoadingWidth.current)}px`;
        widthLocked.current = true;
      }
      return;
    }
    // Release the pin before measuring: with min-width still applied, offsetWidth
    // reports the pinned width, so a label that got shorter would keep the old,
    // larger width and the next loading cycle would pin that instead.
    if (widthLocked.current) {
      element.style.minWidth = '';
      widthLocked.current = false;
    }
    lastNonLoadingWidth.current = element.offsetWidth;
  });

  const slots = iconSlotClasses[size];

  const stamps = {
    'data-variant': resolvedVariant,
    'data-color': color,
    'data-size': size,
    'data-warning': warning || undefined,
    'data-full-width': fullWidth || undefined,
    'data-no-wrap': noWrap || undefined,
    'data-loading': isLoading || undefined,
  };
  const classes = cn(
    buttonVariants({ variant: resolvedVariant, color, size, warning, fullWidth, noWrap }),
    privacyLevel,
    labelClassName,
    effectivelyDisabled && disabledClassName,
    className,
  );

  const content = isLoading ? (
    <>
      {loadingText}
      <LoadingDots />
    </>
  ) : (
    <>
      {startIcon ? (
        <span
          data-slot="start-icon"
          className={cn('inline-flex shrink-0', slots.start, startIconClassName)}
        >
          {startIcon}
        </span>
      ) : null}
      {children}
      {endIcon ? (
        <span
          data-slot="end-icon"
          className={cn('inline-flex shrink-0', slots.end, endIconClassName)}
        >
          {endIcon}
        </span>
      ) : null}
    </>
  );

  if (href !== undefined) {
    // Base UI's Button enforces button semantics and its docs keep links out of
    // its `render` prop (a link through it would answer to Space and carry
    // `role="button"`), so the anchor is rendered directly on the same classes
    // and stamps. Disabled follows MUI's ButtonBase-as-anchor: `href` stays,
    // the link is `aria-disabled`, out of the tab order, its activation is
    // prevented, and `data-disabled` keeps the disabled styles applying.
    return (
      <a
        ref={setRootRef}
        {...stamps}
        data-disabled={effectivelyDisabled ? '' : undefined}
        className={classes}
        style={style}
        href={href}
        aria-disabled={effectivelyDisabled || undefined}
        aria-busy={isLoading || undefined}
        tabIndex={effectivelyDisabled ? -1 : undefined}
        onClick={(event) => {
          if (effectivelyDisabled) {
            event.preventDefault();
            return;
          }
          handleClick(event);
        }}
        {...others}
      >
        {content}
      </a>
    );
  }

  return (
    <ButtonPrimitive
      ref={setRootRef}
      {...stamps}
      className={classes}
      style={style}
      type={type}
      disabled={effectivelyDisabled}
      focusableWhenDisabled={focusableWhenDisabled}
      nativeButton={nativeButton}
      aria-busy={isLoading || undefined}
      onClick={handleClick}
      render={render}
      {...others}
    >
      {content}
    </ButtonPrimitive>
  );
}
